import Button, { ButtonType } from "components/Elements/Buttonv2";
import {
  BranchType,
  ExperimentBranch,
  ExperimentNodeData,
} from "pages/FlowBuilderv2/Nodes/NodeData";
import React, { FC, useEffect, useState } from "react";
import { SidePanelComponentProps } from "../FlowBuilderSidePanel";
import Input from "components/Elements/Inputv2";
import ReactSlider from "react-slider";
import { v4 as uuid } from "uuid";
import { useSidePanelReadOnly } from "../SidePanelReadOnlyContext";

const ExperimentNodeSettings: FC<
  SidePanelComponentProps<ExperimentNodeData>
> = ({ nodeData, setNodeData, setIsError, showErrors }) => {
  const readOnly = useSidePanelReadOnly();
  const [total, setTotal] = useState(1);
  const [remaining, setRemaining] = useState(1);

  const handleChangePercent = (index: number, value: number) => {
    const newData = { ...nodeData };
    const clearValue = +(value / 100).toFixed(2);
    newData.branches[index].ratio = +clearValue.toFixed(2);

    if (nodeData.branches.length === 2) {
      newData.branches[index === 1 ? 0 : 1].ratio = +(1 - clearValue).toFixed(
        2
      );
    }

    setNodeData(newData);
  };

  const handleChangeLabel = (index: number, label: string) => {
    const newData = { ...nodeData };
    newData.branches[index] = {
      ...newData.branches[index],
      label,
    };
    setNodeData(newData);
  };

  const updateAutoWinner = (
    patch: NonNullable<ExperimentNodeData["autoWinner"]>
  ) => {
    setNodeData({
      ...nodeData,
      autoWinner: {
        enabled: false,
        metric: "positive_rate",
        minAssignments: 100,
        targetPositive: 100,
        ...(nodeData.autoWinner || {}),
        ...patch,
      },
    });
  };

  const handleDistribute = () => {
    const newData = { ...nodeData };

    const baseValue = Math.floor((1 / nodeData.branches.length) * 100) / 100;
    const remainder = 1 - baseValue * nodeData.branches.length;
    const distribution = Array(nodeData.branches.length).fill(baseValue);
    distribution[nodeData.branches.length - 1] += remainder;

    distribution.forEach((val, i) => {
      newData.branches[i].ratio = +(val as number).toFixed(2);
    });

    setNodeData(newData);
  };

  const handleAddBranch = () => {
    const newBranch: ExperimentBranch = {
      id: uuid(),
      type: BranchType.EXPERIMENT,
      ratio: 0,
      label: `Variant ${String.fromCharCode(65 + nodeData.branches.length)}`,
    };
    setNodeData({ ...nodeData, branches: [...nodeData.branches, newBranch] });
  };

  const handleDelete = (index: number) => {
    const newData = { ...nodeData };
    const deletedBranchId = newData.branches[index]?.id || `branch-${index}`;
    newData.branches.splice(index, 1);
    if (newData.winnerBranchId === deletedBranchId) {
      newData.winnerBranchId = null;
    }

    setNodeData(newData);
    if (newData.branches.length === 2) handleDistribute();
  };

  useEffect(() => {
    const newTotal = nodeData.branches
      .map((brach) => brach.ratio)
      .reduce((acc, el) => acc + el);

    setTotal(newTotal);

    setRemaining(1 - newTotal);
  }, [nodeData]);

  useEffect(() => {
    setIsError(total > 1);
  }, [total]);

  return (
    <div className={readOnly ? "pointer-events-none select-none" : undefined}>
      <div className="flex flex-col">
        <div className="w-full text-themed-text text-base font-inter font-semibold mb-[10px]">
          Ratio
        </div>
        {nodeData.branches.map((el, i) => {
          return (
            <React.Fragment key={el.id}>
              <div key={el.id} className="relative pb-[20px]">
                <div className="w-full flex items-center justify-between">
                  <div className="w-full flex items-center">
                    <Input
                      type="text"
                      name={`experiment-label-${i}`}
                      id={`experiment-label-${i}`}
                      placeholder={`Variant ${String.fromCharCode(65 + i)}`}
                      className="min-w-[120px]"
                      value={
                        el.label || `Variant ${String.fromCharCode(65 + i)}`
                      }
                      onChange={(value) => handleChangeLabel(i, value)}
                    />
                    <Input
                      type="number"
                      name="hours"
                      id="hours"
                      min={0}
                      max={100}
                      placeholder="percentage"
                      className="max-w-[70px] ml-auto"
                      value={`${String((el.ratio * 100).toFixed(0))}`}
                      onChange={(str) => handleChangePercent(i, +str)}
                    />
                    <span className="text-themed-text text-[14px] font-inter font-semibold ml-1">
                      %
                    </span>
                  </div>
                  {nodeData.branches.length > 2 && (
                    <Button
                      type={ButtonType.LINK}
                      onClick={() => {
                        handleDelete(i);
                      }}
                      className="!text-[#EB5757]"
                    >
                      Delete
                    </Button>
                  )}
                </div>
                <ReactSlider
                  className="h-[20px] flex items-center justify-center"
                  thumbClassName="bg-red-500"
                  trackClassName="h-[4px] bg-[#818CF8] rounded"
                  min={0}
                  max={100}
                  value={el.ratio * 100}
                  onChange={(val) => handleChangePercent(i, val)}
                  renderTrack={(props, state) => (
                    <div
                      {...props}
                      className={`${props.className} ${
                        state.index == 0 ? "!bg-[#818CF8]" : "!bg-themed-border"
                      }`}
                    />
                  )}
                  renderThumb={(props, state) => (
                    <div
                      {...props}
                      className={`rounded-[100%] w-[14px] h-[14px] cursor-grab bg-themed-card border-2 border-[#818CF8]`}
                    />
                  )}
                />
              </div>
            </React.Fragment>
          );
        })}
      </div>
      {nodeData.branches.length > 2 && (
        <>
          <hr className="border-themed-border mb-5" />

          <div className="pb-5 flex flex-col gap-2.5">
            <div className="font-inter text-[14px] font-semibold leading-[22px]">
              In total: {(total * 100).toFixed()} %
            </div>
            <div className="h-[6px] w-full rounded-[100px] bg-themed-border relative">
              <div
                className={`h-full rounded-[100px] ${
                  total === 1 ? "bg-[#22C55E]" : "bg-[#FACC15]"
                }`}
                style={{
                  width: `${(total > 1 ? 1 : total) * 100}%`,
                }}
              />
            </div>
            {remaining > 0 && (
              <div className="font-roboto text-[14px] leading-[22px]">
                Remaining {(remaining * 100).toFixed()}% to be distributed
              </div>
            )}
            {remaining < 0 && (
              <div className="font-roboto text-[14px] leading-[22px] text-[#EB5757]">
                Exceeded by {(remaining * -100).toFixed()}%. Please reduce to
                100%.
              </div>
            )}
          </div>
        </>
      )}
      <div className="relative flex gap-2.5 pb-5">
        {nodeData.branches.length === 2 && (
          <div className="w-[calc(100%+20px)] absolute left-[-20px] z-10 bottom-0 border-themed-border border-t-[1px]" />
        )}
        <Button
          type={ButtonType.SECONDARY}
          onClick={handleAddBranch}
          className="!text-themed-text !border-themed-border"
        >
          Add branch
        </Button>
        <Button
          type={ButtonType.SECONDARY}
          onClick={handleDistribute}
          className="!text-themed-text !border-themed-border"
        >
          Distribute evenly
        </Button>
      </div>
      <div className="border-t border-themed-border pt-5 flex flex-col gap-4">
        <div>
          <div className="text-themed-text text-base font-inter font-semibold mb-2">
            Winner routing
          </div>
          <div className="text-[12px] leading-5 text-themed-textSecondary mb-2">
            Leave unlocked to keep splitting traffic. Choose a winner to send
            every new contact down that branch.
          </div>
          <select
            className="w-full border border-themed-border rounded px-[12px] py-[6px] bg-themed-card text-themed-text text-[14px]"
            value={nodeData.winnerBranchId || ""}
            onChange={(event) =>
              setNodeData({
                ...nodeData,
                winnerBranchId: event.target.value || null,
              })
            }
          >
            <option value="">No locked winner</option>
            {nodeData.branches.map((branch, index) => (
              <option
                key={branch.id || index}
                value={branch.id || `branch-${index}`}
              >
                {branch.label || `Variant ${String.fromCharCode(65 + index)}`}
              </option>
            ))}
          </select>
        </div>

        <label className="flex items-center gap-2 text-[14px] text-themed-text">
          <input
            type="checkbox"
            checked={!!nodeData.autoWinner?.enabled}
            onChange={(event) =>
              updateAutoWinner({ enabled: event.target.checked })
            }
          />
          Auto-lock winner
        </label>

        {nodeData.autoWinner?.enabled && (
          <div className="flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-[13px] text-themed-text">
              Metric
              <select
                className="w-full border border-themed-border rounded px-[12px] py-[6px] bg-themed-card text-themed-text text-[14px]"
                value={nodeData.autoWinner?.metric || "positive_rate"}
                onChange={(event) =>
                  updateAutoWinner({
                    metric: event.target.value as
                      | "positive_rate"
                      | "positive_count",
                  })
                }
              >
                <option value="positive_rate">
                  Highest positive rate after sample size
                </option>
                <option value="positive_count">
                  First to target positive count
                </option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-[13px] text-themed-text">
              Minimum assignments
              <Input
                type="number"
                min={1}
                value={String(nodeData.autoWinner?.minAssignments || 100)}
                onChange={(value) =>
                  updateAutoWinner({ minAssignments: Number(value) || 1 })
                }
                className="w-full"
                wrapperClassName="w-full"
              />
            </label>
            <label className="flex flex-col gap-1 text-[13px] text-themed-text">
              Target positive outcomes
              <Input
                type="number"
                min={1}
                value={String(nodeData.autoWinner?.targetPositive || 100)}
                onChange={(value) =>
                  updateAutoWinner({ targetPositive: Number(value) || 1 })
                }
                className="w-full"
                wrapperClassName="w-full"
              />
            </label>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExperimentNodeSettings;
